import { DefaultValues } from 'react-hook-form'
import { BillingAddress, OrderPaymentMetadata, ShippingAddress } from '@lib/api'
import { CreateAddressInput } from '@lib/api/vendureGraphql'
import { OrderData } from '@lib/api/order/getActiveCustomerOrders'
import { AddressFormData } from './ShippingAddressView/useAddressForm'

export const orderStates = [
  'ArrangingPayment',
  'PaymentAccepted',
  'PaymentSettled',
  'PartiallyShipped',
  'Shipped',
  'PartiallyDelivered',
  'Delivered',
  'Cancelled',
] as const

export type ValidOrderState = typeof orderStates[number]

interface TaxSummary {
  taxTotal: number
}

export const getOrderAddress = (data: AddressFormData): CreateAddressInput => ({
  countryCode: data.zone,
  streetLine1: data.address,
  streetLine2: data.addressDetails,
  city: data.city,
  fullName: `${data.firstName} ${data.lastName}`,
  postalCode: data.zipcode,
  phoneNumber: data.phone,
  province: data.province,
  customFields: {
    email: data.email,
    firstName: data.firstName,
    lastName: data.lastName,
  },
})

export const getShippingFormInput = (
  shippingAddress: ShippingAddress
): DefaultValues<AddressFormData> => ({
  zone: shippingAddress?.countryCode ?? undefined,
  address: shippingAddress?.streetLine1 ?? undefined,
  addressDetails: shippingAddress?.streetLine2 ?? undefined,
  city: shippingAddress?.city ?? undefined,
  zipcode: shippingAddress?.postalCode ?? undefined,
  phone: shippingAddress?.phoneNumber ?? undefined,
  email: shippingAddress?.customFields?.email ?? undefined,
  firstName: shippingAddress?.customFields?.firstName ?? undefined,
  lastName: shippingAddress?.customFields?.lastName ?? undefined,
  province: shippingAddress?.province ?? undefined,
})

export const getBillingFormInput = (
  billingAddress: BillingAddress
): DefaultValues<AddressFormData> => ({
  zone: billingAddress?.countryCode ?? undefined,
  address: billingAddress?.streetLine1 ?? undefined,
  addressDetails: billingAddress?.streetLine2 ?? undefined,
  city: billingAddress?.city ?? undefined,
  zipcode: billingAddress?.postalCode ?? undefined,
  phone: billingAddress?.phoneNumber ?? undefined,
  email: billingAddress?.customFields?.email ?? undefined,
  firstName: billingAddress?.customFields?.firstName ?? undefined,
  lastName: billingAddress?.customFields?.lastName ?? undefined,
  province: billingAddress?.province ?? undefined,
})

export const areAddressesEqual = (
  address1?: DefaultValues<AddressFormData>,
  address2?: DefaultValues<AddressFormData>
): boolean => {
  if (!address1 || !address2) return false

  return (
    address1.address === address2.address &&
    address1.addressDetails === address2.addressDetails &&
    address1.city === address2.city &&
    address1.email === address2.email &&
    address1.firstName === address2.firstName &&
    address1.lastName === address2.lastName &&
    address1.phone === address2.phone &&
    address1.province === address2.province &&
    address1.zone === address2.zone &&
    address1.zipcode === address2.zipcode
  )
}

export const calculateTaxes = (taxSummary: TaxSummary[]): number => {
  return taxSummary.reduce((sum, current) => current.taxTotal + sum, 0)
}
export const isValidOrderState = (
  state: string | null
): state is ValidOrderState => orderStates.includes(state as ValidOrderState)

export const getPaymentMetadata = (
  order: OrderData
): OrderPaymentMetadata | null => {
  if (
    !isValidOrderState(order.state) ||
    !order.payments ||
    order.payments.length < 1
  ) {
    return null
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const metadata = order.payments[0].metadata

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  if (metadata === undefined || metadata.public === undefined) {
    // throw new Error(`payment metadata for order ${order.id} are not valid`)
    return null
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
  return metadata.public
}
